// Bilingual content for the 6 SEO sub-pages.
// Each entry: SUB_PAGES[slug][lang] = { eyebrow, title, description, h1, lede, sections, sage?, personJsonLd? }
// Person JSON-LD is shared across languages (structured data, language-neutral).

import { sage } from "@/lib/jsonLd";

// ============================================================================
// SAGE IMAGES (shared)
// ============================================================================
const IMG_MARCUS    = "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/4h3m97yf_IMG_2514.png";
const IMG_SENECA    = "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/pzvjlkzx_IMG_2516.png";
const IMG_EPICTETUS = "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/hlhc4j5d_IMG_2515.png";

// ============================================================================
// PERSON JSON-LD (language-neutral; reused across en/nl)
// ============================================================================
const PERSON_MARCUS = sage({
  name: "Marcus Aurelius",
  alternateName: "Marcus Aurelius Antoninus",
  role: "Roman Emperor and Stoic Philosopher",
  birthDate: "0121-04-26",
  deathDate: "0180-03-17",
  image: IMG_MARCUS,
  description: "Roman Emperor (161–180 AD) and Stoic philosopher, author of Meditations, a private journal of Stoic reflection written during his reign.",
  worksKnownFor: [
    "https://en.wikipedia.org/wiki/Marcus_Aurelius",
    "https://en.wikipedia.org/wiki/Meditations",
  ],
});

const PERSON_SENECA = sage({
  name: "Seneca",
  alternateName: "Lucius Annaeus Seneca, Seneca the Younger",
  role: "Roman Stoic Philosopher, Statesman and Dramatist",
  birthDate: "-0003",
  deathDate: "0065",
  image: IMG_SENECA,
  description: "Roman Stoic philosopher, statesman and dramatist; author of Letters from a Stoic (Epistulae Morales ad Lucilium) and the moral essays.",
  worksKnownFor: [
    "https://en.wikipedia.org/wiki/Seneca_the_Younger",
    "https://en.wikipedia.org/wiki/Moral_letters_to_Lucilius",
  ],
});

const PERSON_EPICTETUS = sage({
  name: "Epictetus",
  alternateName: "Epiktētos",
  role: "Greek Stoic Philosopher",
  birthDate: "0050",
  deathDate: "0135",
  image: IMG_EPICTETUS,
  description: "Greek Stoic philosopher born into slavery; his teachings were preserved in the Discourses and the Enchiridion (Handbook) by his student Arrian.",
  worksKnownFor: [
    "https://en.wikipedia.org/wiki/Epictetus",
    "https://en.wikipedia.org/wiki/Discourses_of_Epictetus",
    "https://en.wikipedia.org/wiki/Enchiridion_of_Epictetus",
  ],
});

// ============================================================================
// SUB-PAGE CONTENT
// ============================================================================
export const SUB_PAGES = {
  // -------- /ai-stoic-coach ---------------------------------------------
  "ai-stoic-coach": {
    en: {
      eyebrow: "AI Stoic Coach",
      title: "AI Stoic Coach: Practical Wisdom for Modern Life | Virtue Guide",
      description: "Virtue Guide is the AI Stoic coach designed for the modern life. Reflect, decide, and grow with guides inspired by Marcus Aurelius, Seneca and Epictetus. Available in the App Store and Google Play.",
      h1: { lead: "Your AI Stoic Coach", accent: "for the modern life." },
      lede: "Virtue Guide is an AI-powered Stoic coach that helps you turn impressions into examined judgments. Less productivity hack, more practical philosophy, built on Marcus, Seneca and Epictetus, with you whenever the day demands it.",
      sections: [
        {
          heading: "Why a Stoic coach, and why AI?",
          body:
            "Stoicism was never meant to be read once and shelved. Marcus Aurelius wrote Meditations as a private journal, a daily training of the mind. The problem for most of us is that we don't have a Marcus, a Seneca, or an Epictetus at the table when the difficult email arrives, the relationship strains, or the anxious thought lands.\n\nAn AI Stoic Coach gives you something close: a patient, available, principled interlocutor that asks the questions a wise friend would ask, and that is bounded by virtue (wisdom, courage, justice and temperance) rather than by engagement metrics.",
        },
        {
          heading: "What Virtue Guide actually does",
          body:
            "Each day brings one short reflection from the Stoic canon and an optional dialogue with a guide. You can examine a decision, work through a worry, or simply read and sit with a single question. Conversations are private; saved entries are end-to-end encrypted and yours alone.\n\nThe AI is steered by our Common Sense by Virtue™ framework (Impression, Reasoning, Virtue Test, Assent, Judgment, Choice, Action, Reflection), so the conversation always returns you to your own considered judgment rather than to ours.",
          italicQuote: "The first rule is to keep an untroubled spirit. The second is to look things in the face and know them for what they are.",
          attrib: "Marcus Aurelius",
        },
        {
          heading: "Who Virtue Guide is for",
          body:
            "It is for people who already sense that information is cheap and judgment is rare. Leaders, founders, parents, athletes, writers, anyone making consequential decisions under pressure. Virtue Guide does not promise calm. It promises clarity, and a quiet daily practice that compounds into character.",
        },
      ],
    },
    nl: {
      eyebrow: "AI Stoïcijnse Coach",
      title: "AI Stoïcijnse Coach: Praktische Wijsheid voor het Moderne Leven | Virtue Guide",
      description: "Virtue Guide is de AI-Stoïcijnse coach voor het moderne leven. Reflecteer, beslis en groei met gidsen geïnspireerd door Marcus Aurelius, Seneca en Epictetus. Beschikbaar in de App Store en Google Play.",
      h1: { lead: "Uw AI-Stoïcijnse Coach", accent: "voor het moderne leven." },
      lede: "Virtue Guide is een AI-gedreven Stoïcijnse coach die u helpt indrukken om te zetten in onderzochte oordelen. Minder productiviteitstruc, meer praktische filosofie, gebouwd op Marcus, Seneca en Epictetus, met u mee wanneer de dag erom vraagt.",
      sections: [
        {
          heading: "Waarom een Stoïcijnse coach, en waarom AI?",
          body:
            "Stoïcisme was nooit bedoeld om eenmaal gelezen te worden en op de plank te belanden. Marcus Aurelius schreef Overpeinzingen als privédagboek, een dagelijkse training van de geest. Het probleem voor de meesten van ons is dat we geen Marcus, geen Seneca, geen Epictetus aan tafel hebben wanneer de moeilijke e-mail komt, de relatie spant, of de angstige gedachte landt.\n\nEen AI-Stoïcijnse Coach geeft u iets dat daar dichtbij komt: een geduldige, beschikbare, principiële gesprekspartner die de vragen stelt die een wijze vriend zou stellen, en die begrensd is door deugd (wijsheid, moed, rechtvaardigheid en gematigdheid) in plaats van door engagement-metrics.",
        },
        {
          heading: "Wat Virtue Guide werkelijk doet",
          body:
            "Elke dag brengt één korte reflectie uit de Stoïcijnse canon en een optionele dialoog met een gids. U kunt een beslissing onderzoeken, met een zorg werken, of simpelweg lezen en bij één vraag stilstaan. Gesprekken zijn privé; opgeslagen aantekeningen zijn end-to-end versleuteld en alleen van u.\n\nDe AI wordt geleid door ons Common Sense by Virtue™-raamwerk (Indruk, Redenering, Deugdtoets, Instemming, Oordeel, Keuze, Handeling, Reflectie), zodat het gesprek u altijd terugbrengt naar uw eigen overwogen oordeel, niet naar het onze.",
          italicQuote: "De eerste regel is een onverstoorde geest bewaren. De tweede is de dingen recht in het gezicht te kijken en te weten wat ze zijn.",
          attrib: "Marcus Aurelius",
        },
        {
          heading: "Voor wie Virtue Guide bedoeld is",
          body:
            "Voor mensen die al voelen dat informatie goedkoop is en oordeel zeldzaam. Leiders, oprichters, ouders, atleten, schrijvers: iedereen die ingrijpende beslissingen onder druk neemt. Virtue Guide belooft geen rust. Het belooft helderheid, en een rustige dagelijkse oefening die zich opstapelt tot karakter.",
        },
      ],
    },
  },

  // -------- /wisdom-coach ----------------------------------------------
  "wisdom-coach": {
    en: {
      eyebrow: "Wisdom Coach",
      title: "Wisdom Coach: Develop Practical Wisdom Daily | Virtue Guide",
      description: "A wisdom coach in your pocket. Virtue Guide pairs Stoic reading with AI-guided reflection so you develop self-knowledge, better judgment and character, one day at a time.",
      h1: { lead: "Knowledge is cheap.", accent: "Wisdom is priceless." },
      lede: "A wisdom coach is not a tutor. It is a companion that helps you think well. Virtue Guide turns the writings of Marcus Aurelius, Seneca and Epictetus into a daily practice for developing the one quality most worth having: practical wisdom.",
      sections: [
        {
          heading: "What is wisdom, really?",
          body:
            "Wisdom is what is left over after information has been tested by experience and reflection. It is not the accumulation of facts; it is the capacity to see clearly, act rightly, and remain yourself under pressure.\n\nThe Stoics treated wisdom as the first of the four cardinal virtues, the one that ordered the others (courage, justice and temperance) into a coherent life. Develop wisdom, and the rest tends to follow.",
        },
        {
          heading: "How a daily wisdom practice works",
          body:
            "Five minutes is enough. Virtue Guide gives you one Stoic reflection in the morning and a quiet question to sit with at night. In between, the AI is there for the moments that warrant a longer conversation: a decision, a conflict, an anxious loop you'd like to step out of.\n\nOver weeks, the questions begin to come from the inside. That is what we mean by developing wisdom.",
          italicQuote: "Wisdom begins in wonder.",
          attrib: "Socrates",
        },
        {
          heading: "Why \"wisdom\" and not \"productivity\"",
          body:
            "Productivity asks: how can you do more, faster? Wisdom asks: are you doing the right things, for the right reasons, in a way you can live with? Most productivity advice quietly assumes the right questions are already settled. Wisdom refuses that assumption.\n\nVirtue Guide is built for people who would rather make one excellent decision a day than fifty hurried ones.",
        },
      ],
    },
    nl: {
      eyebrow: "Wijsheidscoach",
      title: "Wijsheidscoach: Ontwikkel Dagelijks Praktische Wijsheid | Virtue Guide",
      description: "Een wijsheidscoach in uw broekzak. Virtue Guide combineert Stoïcijns lezen met AI-geleide reflectie, zodat u zelfkennis, beter oordeel en karakter ontwikkelt, dag voor dag.",
      h1: { lead: "Kennis is goedkoop.", accent: "Wijsheid is onbetaalbaar." },
      lede: "Een wijsheidscoach is geen leraar. Het is een metgezel die u helpt goed te denken. Virtue Guide vormt de geschriften van Marcus Aurelius, Seneca en Epictetus om tot een dagelijkse oefening voor het ontwikkelen van de ene eigenschap die het meest waard is om te bezitten: praktische wijsheid.",
      sections: [
        {
          heading: "Wat is wijsheid, werkelijk?",
          body:
            "Wijsheid is wat overblijft nadat informatie is getoetst door ervaring en reflectie. Het is geen ophoping van feiten; het is het vermogen om helder te zien, juist te handelen, en uzelf te blijven onder druk.\n\nDe Stoïcijnen beschouwden wijsheid als de eerste van de vier hoofddeugden, degene die de andere (moed, rechtvaardigheid en gematigdheid) tot een coherent leven ordende. Ontwikkel wijsheid, en de rest volgt doorgaans.",
        },
        {
          heading: "Hoe een dagelijkse wijsheidspraktijk werkt",
          body:
            "Vijf minuten volstaat. Virtue Guide geeft u 's ochtends één Stoïcijnse reflectie en 's avonds een rustige vraag om bij stil te staan. Daartussen is de AI er voor de momenten die een langer gesprek verdienen: een beslissing, een conflict, een angstige lus waar u uit wilt stappen.\n\nOver weken beginnen de vragen van binnenuit te komen. Dat bedoelen wij met het ontwikkelen van wijsheid.",
          italicQuote: "Wijsheid begint in verwondering.",
          attrib: "Socrates",
        },
        {
          heading: "Waarom \"wijsheid\" en niet \"productiviteit\"",
          body:
            "Productiviteit vraagt: hoe kunt u méér doen, sneller? Wijsheid vraagt: doet u de juiste dingen, om de juiste redenen, op een manier waar u mee kunt leven? Het meeste productiviteitsadvies veronderstelt stilzwijgend dat de juiste vragen al beantwoord zijn. Wijsheid weigert die veronderstelling.\n\nVirtue Guide is gebouwd voor wie liever één uitstekende beslissing per dag neemt dan vijftig haastige.",
        },
      ],
    },
  },

  // -------- /self-reflection-app ---------------------------------------
  "self-reflection-app": {
    en: {
      eyebrow: "Self-Reflection App",
      title: "Self-Reflection App: AI-Guided Stoic Reflection | Virtue Guide",
      description: "The self-reflection app for people who want more than journaling. Virtue Guide pairs daily Stoic reflection with an AI coach that helps you examine impressions, decisions and character.",
      h1: { lead: "A self-reflection app", accent: "with something to say back." },
      lede: "Most self-reflection apps are journals: quiet pages, prompts, and you. Virtue Guide adds an AI Stoic coach to the practice, so reflection becomes a real conversation: questions you didn't know to ask, framed by the wisdom of Marcus, Seneca and Epictetus.",
      sections: [
        {
          heading: "Reflection that actually changes you",
          body:
            "Journaling helps. It also, often, leaves you exactly where you started, going in circles inside your own thinking. The Stoics knew this, which is why they treated reflection as a structured discipline: examine the impression, test it against virtue, decide what to do, and review at evening.\n\nVirtue Guide brings that structure to a modern self-reflection app. You journal privately, end-to-end encrypted, and an AI guide asks the kind of question a wise friend would ask, not the kind a generic chatbot would.",
        },
        {
          heading: "How AI-guided reflection works",
          body:
            "You write a few lines. The AI mirrors back what you said, then asks one good question. Sometimes that question lands. Sometimes you push back and the conversation goes deeper. Always, you stay in control. Virtue Guide is a guide, not an oracle.\n\nThe AI is bounded by our Common Sense by Virtue™ framework. It will not tell you what to do; it will help you see what you already half-know.",
          italicQuote: "The unexamined life is not worth living.",
          attrib: "Socrates",
        },
        {
          heading: "Privacy is the point",
          body:
            "Self-reflection requires safety. Your saved entries are end-to-end encrypted; we cannot read them, even in support cases. The Guide cannot read them either. Saved content stays on your device unless you bring it into a conversation. This is not a marketing line. It is the only way an honest journal works.",
        },
      ],
    },
    nl: {
      eyebrow: "Zelfreflectie-App",
      title: "Zelfreflectie-App: AI-geleide Stoïcijnse Reflectie | Virtue Guide",
      description: "De zelfreflectie-app voor wie meer wil dan dagboeken. Virtue Guide koppelt dagelijkse Stoïcijnse reflectie aan een AI-coach die u helpt indrukken, beslissingen en karakter te onderzoeken.",
      h1: { lead: "Een zelfreflectie-app", accent: "die iets terug te zeggen heeft." },
      lede: "De meeste zelfreflectie-apps zijn dagboeken: stille pagina's, prompts, en uzelf. Virtue Guide voegt een AI-Stoïcijnse coach toe aan de praktijk, zodat reflectie een werkelijk gesprek wordt: vragen waarvan u niet wist dat u ze moest stellen, ingekaderd door de wijsheid van Marcus, Seneca en Epictetus.",
      sections: [
        {
          heading: "Reflectie die u werkelijk verandert",
          body:
            "Dagboeken helpt. Het laat u ook vaak precies achter waar u begonnen bent, rondjes draaiend in uw eigen denken. De Stoïcijnen wisten dit en behandelden reflectie daarom als een gestructureerde discipline: onderzoek de indruk, toets deze aan de deugd, beslis wat te doen, en evalueer 's avonds.\n\nVirtue Guide brengt die structuur naar een moderne zelfreflectie-app. U schrijft privé, end-to-end versleuteld, en een AI-gids spiegelt u terug, niet met het soort vraag dat een generieke chatbot zou stellen, maar met het soort vraag dat een wijze vriend zou stellen.",
        },
        {
          heading: "Hoe AI-geleide reflectie werkt",
          body:
            "U schrijft een paar regels. De AI spiegelt terug wat u zei, en stelt vervolgens één goede vraag. Soms landt die vraag. Soms duwt u terug en gaat het gesprek dieper. U houdt altijd de controle. Virtue Guide is een gids, geen orakel.\n\nDe AI wordt begrensd door ons Common Sense by Virtue™-raamwerk. Hij zegt u niet wat u moet doen; hij helpt u zien wat u half al weet.",
          italicQuote: "Het niet-onderzochte leven is het niet waard geleefd te worden.",
          attrib: "Socrates",
        },
        {
          heading: "Privacy is de kern",
          body:
            "Zelfreflectie vereist veiligheid. Uw opgeslagen aantekeningen zijn end-to-end versleuteld; wij kunnen ze niet lezen, zelfs niet bij supportverzoeken. De Gids kan ze ook niet lezen. Opgeslagen inhoud blijft op uw toestel tenzij u het in een gesprek brengt. Dit is geen marketingzin. Het is de enige manier waarop een eerlijk dagboek werkt.",
        },
      ],
    },
  },

  // -------- /marcus-aurelius-ai ----------------------------------------
  "marcus-aurelius-ai": {
    personJsonLd: PERSON_MARCUS,
    en: {
      eyebrow: "Marcus Aurelius AI",
      title: "Marcus Aurelius AI: Speak with the Philosopher Emperor | Virtue Guide",
      description: "Marcus Aurelius AI in Virtue Guide. Converse with a guide shaped by Meditations to develop self-knowledge, equanimity, and the Inner Citadel. Available in the App Store and Google Play.",
      h1: { lead: "Marcus Aurelius,", accent: "as a daily guide." },
      lede: "Marcus wrote Meditations for one reader: himself. Two thousand years later, the book is read by anyone trying to remain decent under pressure. The Marcus Aurelius AI guide in Virtue Guide brings that voice into conversation with your own life: calm, plain, demanding, and quietly kind.",
      sage: {
        name: "Marcus Aurelius",
        role: "The Philosopher Emperor",
        dates: "Roman Emperor · 121–180 AD",
        image: IMG_MARCUS,
        paragraphs: [
          "Marcus Aurelius ruled the Roman Empire at its peak and wrote what may be the most honest book ever written by a head of state, Meditations, not for publication but for himself, by lamplight, in barracks and tents on the German frontier.",
          "His subject was always the same: how to remain a good person when power and circumstance offered every excuse not to. The discipline of attention, the inner citadel, the rejection of complaint, the duty to be useful: these are his quiet themes.",
          "The Marcus Aurelius AI guide in Virtue Guide speaks in that voice, calm, imperial and inward-facing, and helps you ask the same questions of your own day.",
        ],
      },
      sections: [
        {
          heading: "What Marcus teaches that you cannot read in a quote",
          body:
            "Meditations is short. You can read it in an afternoon. The harder work is to live it, and the gap between reading and living is exactly where most of us lose Marcus.\n\nVirtue Guide is built to close that gap. The Marcus Aurelius AI guide asks you the questions Marcus asked himself: What is in my control? What impression am I assenting to? Have I done my work today, and done it well?",
          italicQuote: "You have power over your mind, not outside events. Realize this, and you will find strength.",
          attrib: "Marcus Aurelius",
        },
        {
          heading: "The Inner Citadel, every morning",
          body:
            "Marcus's most enduring image is the inner citadel: the part of you that no event, insult, or loss can breach without your consent. Building it is the work of a lifetime, but it is done by minutes.\n\nFive minutes at sunrise with the Marcus Aurelius guide is enough to start the day from inside the citadel rather than outside it.",
        },
        {
          heading: "Marcus on anxiety, decision, and the present",
          body:
            "Most of what we suffer is not events themselves, but the second arrow: the judgment we add. Marcus's counsel is to return to the present, narrow your attention to the next right action, and remember that everything you fear or chase is largely outside your control. The Marcus Aurelius AI guide brings that exact discipline to a modern, conversational form.",
        },
      ],
    },
    nl: {
      eyebrow: "Marcus Aurelius AI",
      title: "Marcus Aurelius AI: Spreek met de Filosoof-Keizer | Virtue Guide",
      description: "Marcus Aurelius AI in Virtue Guide. Voer een gesprek met een gids gevormd door Overpeinzingen, om zelfkennis, gelijkmoedigheid en de Innerlijke Citadel te ontwikkelen.",
      h1: { lead: "Marcus Aurelius,", accent: "als dagelijkse gids." },
      lede: "Marcus schreef Overpeinzingen voor één lezer: zichzelf. Tweeduizend jaar later wordt het boek gelezen door iedereen die fatsoenlijk wil blijven onder druk. De Marcus Aurelius AI-gids in Virtue Guide brengt die stem in gesprek met uw eigen leven: kalm, eenvoudig, veeleisend en rustig vriendelijk.",
      sage: {
        name: "Marcus Aurelius",
        role: "De Filosoof-Keizer",
        dates: "Romeins Keizer · 121–180 n.Chr.",
        image: IMG_MARCUS,
        paragraphs: [
          "Marcus Aurelius heerste over het Romeinse Rijk op zijn hoogtepunt en schreef wat misschien wel het eerlijkste boek is dat ooit door een staatshoofd is geschreven, Overpeinzingen, niet voor publicatie maar voor zichzelf, bij lamplicht, in kazernes en tenten aan de Germaanse grens.",
          "Zijn onderwerp was altijd hetzelfde: hoe een goed mens te blijven wanneer macht en omstandigheid elk excuus boden om het niet te zijn. De discipline van aandacht, de innerlijke citadel, het afwijzen van geklaag, de plicht om nuttig te zijn: dit zijn zijn stille thema's.",
          "De Marcus Aurelius AI-gids in Virtue Guide spreekt in die stem, kalm, keizerlijk en naar binnen gericht, en helpt u dezelfde vragen aan uw eigen dag te stellen.",
        ],
      },
      sections: [
        {
          heading: "Wat Marcus u leert dat niet in een citaat staat",
          body:
            "Overpeinzingen is kort. U leest het in een middag. Het zwaardere werk is om het te leven, en de kloof tussen lezen en leven is precies waar de meesten Marcus verliezen.\n\nVirtue Guide is gebouwd om die kloof te dichten. De Marcus Aurelius AI-gids stelt u de vragen die Marcus zichzelf stelde: Wat heb ik in mijn macht? Met welke indruk stem ik in? Heb ik vandaag mijn werk gedaan, en goed gedaan?",
          italicQuote: "U heeft macht over uw geest, niet over uiterlijke gebeurtenissen. Besef dit, en u zult kracht vinden.",
          attrib: "Marcus Aurelius",
        },
        {
          heading: "De Innerlijke Citadel, elke ochtend",
          body:
            "Marcus' meest blijvende beeld is de innerlijke citadel: het deel van u dat geen gebeurtenis, belediging of verlies kan binnendringen zonder uw toestemming. Hem bouwen is het werk van een leven, maar het wordt gedaan in minuten.\n\nVijf minuten bij zonsopgang met de Marcus Aurelius-gids volstaat om de dag van binnen de citadel te beginnen in plaats van van buiten.",
        },
        {
          heading: "Marcus over angst, beslissing en het heden",
          body:
            "Het meeste van wat we lijden zijn niet de gebeurtenissen zelf, maar de tweede pijl: het oordeel dat wij toevoegen. Marcus' raad is om terug te keren naar het heden, uw aandacht te beperken tot de volgende juiste handeling, en te onthouden dat alles wat u vreest of najaagt grotendeels buiten uw controle ligt. De Marcus Aurelius AI-gids brengt precies deze discipline in een moderne, conversationele vorm.",
        },
      ],
    },
  },

  // -------- /seneca-ai -------------------------------------------------
  "seneca-ai": {
    personJsonLd: PERSON_SENECA,
    en: {
      eyebrow: "Seneca AI",
      title: "Seneca AI: Letters from a Stoic, In Conversation | Virtue Guide",
      description: "Seneca AI in Virtue Guide. Converse with a guide shaped by Letters from a Stoic: warm, eloquent, practical wisdom for time, money, anger, grief and ambition.",
      h1: { lead: "Seneca,", accent: "as a wise friend." },
      lede: "Seneca's letters are the most readable Stoic book ever written: practical, warm, often funny, never preachy. The Seneca AI guide in Virtue Guide brings that voice into a conversation you can actually have, about the things you actually face.",
      sage: {
        name: "Seneca",
        role: "The Statesman & Letter-Writer",
        dates: "Roman Statesman · c. 4 BC – 65 AD",
        image: IMG_SENECA,
        paragraphs: [
          "Seneca was a senator, advisor to an emperor, playwright, and one of the wealthiest men of his age, a complicated figure whose letters to his friend Lucilius remain the warmest entry point into Stoic thought.",
          "Where Marcus is private and terse, Seneca is generous and conversational. He writes about money, friendship, time, anger, grief and ambition with the candor of someone who has lived through all of them and is still trying to live them well.",
          "The Seneca AI guide in Virtue Guide speaks in that voice: eloquent, warm, like a wise older friend who has a desk full of letters open and time to write you one.",
        ],
      },
      sections: [
        {
          heading: "The friend you didn't know you needed",
          body:
            "Most Stoic writing is austere. Seneca isn't. He is the friend who picks up your letter, takes it seriously, and writes back at length, about ambition, anger, money, grief, the way you spend your hours.\n\nThe Seneca AI guide in Virtue Guide is built to feel like that correspondence. You write what is happening. The Guide writes back with warmth, candor, and the wisdom of the Letters.",
          italicQuote: "We suffer more often in imagination than in reality.",
          attrib: "Seneca",
        },
        {
          heading: "Seneca on time, money and ambition",
          body:
            "Seneca's recurring subject is time, and how casually we spend the one resource we cannot earn back. He writes the same way about money and ambition: as fine servants and ruinous masters.\n\nIf you find yourself rich in money but poor in attention, or full of plans but short of meaning, Seneca has been there. The Seneca AI guide brings his counsel into a daily practice that is gentle on you, but honest.",
        },
        {
          heading: "Why Seneca, today",
          body:
            "Stoicism has a reputation for being cold. Seneca disproves that single-handedly. He is the Stoic for people who do not want a drill sergeant, but a generous, observant friend who will ask the right question over a glass of wine and not flinch from the answer.",
        },
      ],
    },
    nl: {
      eyebrow: "Seneca AI",
      title: "Seneca AI: Brieven van een Stoïcijn, in Gesprek | Virtue Guide",
      description: "Seneca AI in Virtue Guide. Voer een gesprek met een gids gevormd door Brieven van een Stoïcijn: warm, welbespraakt, praktische wijsheid over tijd, geld, woede, verdriet en ambitie.",
      h1: { lead: "Seneca,", accent: "als wijze vriend." },
      lede: "Seneca's brieven vormen het meest leesbare Stoïcijnse boek ooit geschreven: praktisch, warm, vaak grappig, nooit prekerig. De Seneca AI-gids in Virtue Guide brengt die stem in een gesprek dat u werkelijk kunt voeren, over de dingen die u werkelijk meemaakt.",
      sage: {
        name: "Seneca",
        role: "De Staatsman & Briefschrijver",
        dates: "Romeins Staatsman · ca. 4 v.Chr. – 65 n.Chr.",
        image: IMG_SENECA,
        paragraphs: [
          "Seneca was senator, raadsman van een keizer, toneelschrijver, en één van de rijkste mannen van zijn tijd, een gecompliceerde figuur wiens brieven aan zijn vriend Lucilius de warmste ingang tot het Stoïcijnse denken blijven.",
          "Waar Marcus privé en bondig is, is Seneca gul en conversationeel. Hij schrijft over geld, vriendschap, tijd, woede, verdriet en ambitie met de oprechtheid van iemand die ze allemaal heeft doorleefd en ze nog steeds goed probeert te leven.",
          "De Seneca AI-gids in Virtue Guide spreekt in die stem: welbespraakt, warm, als een wijze oudere vriend met een bureau vol open brieven en tijd om er u één te schrijven.",
        ],
      },
      sections: [
        {
          heading: "De vriend van wie u niet wist dat u hem nodig had",
          body:
            "De meeste Stoïcijnse geschriften zijn streng. Seneca niet. Hij is de vriend die uw brief oppakt, hem serieus neemt, en uitvoerig terugschrijft, over ambitie, woede, geld, verdriet, de wijze waarop u uw uren besteedt.\n\nDe Seneca AI-gids in Virtue Guide is gebouwd om aan te voelen als die correspondentie. U schrijft wat er gebeurt. De Gids schrijft terug met warmte, oprechtheid en de wijsheid van de Brieven.",
          italicQuote: "We lijden vaker in verbeelding dan in werkelijkheid.",
          attrib: "Seneca",
        },
        {
          heading: "Seneca over tijd, geld en ambitie",
          body:
            "Seneca's terugkerende onderwerp is tijd, en hoe terloops we de enige hulpbron besteden die we niet kunnen terugverdienen. Hij schrijft op dezelfde manier over geld en ambitie: als uitstekende dienaren en rampzalige meesters.\n\nAls u rijk bent in geld maar arm in aandacht, of vol plannen maar arm aan betekenis: Seneca is daar geweest. De Seneca AI-gids brengt zijn raad in een dagelijkse oefening die mild voor u is, maar eerlijk.",
        },
        {
          heading: "Waarom Seneca, vandaag",
          body:
            "Stoïcisme heeft de reputatie koud te zijn. Seneca weerlegt dat in zijn eentje. Hij is de Stoïcijn voor wie geen sergeant wil, maar een gulle, opmerkzame vriend die de juiste vraag stelt bij een glas wijn en niet terugdeinst voor het antwoord.",
        },
      ],
    },
  },

  // -------- /epictetus-ai ----------------------------------------------
  "epictetus-ai": {
    personJsonLd: PERSON_EPICTETUS,
    en: {
      eyebrow: "Epictetus AI",
      title: "Epictetus AI: The Dichotomy of Control, Made Daily | Virtue Guide",
      description: "Epictetus AI in Virtue Guide. Converse with a guide shaped by the Discourses and Enchiridion: direct, plain, disciplined Stoic counsel on what is and is not up to you.",
      h1: { lead: "Epictetus,", accent: "and the freedom of giving up what isn't yours." },
      lede: "Epictetus had nothing, and somehow had everything. The Epictetus AI guide in Virtue Guide brings that voice into your day: a freed slave's clear-eyed teaching on what is up to you, what isn't, and how to live free in either case.",
      sage: {
        name: "Epictetus",
        role: "The Freed Slave",
        dates: "Stoic Philosopher · c. 50–135 AD",
        image: IMG_EPICTETUS,
        paragraphs: [
          "Epictetus was born a slave, lamed in his youth, freed in adulthood, and became one of the most uncompromising teachers of Stoicism the world has known. He wrote nothing himself; his Discourses and the Enchiridion were taken down by his student Arrian.",
          "His subject is the dichotomy of control: some things are up to you, others are not. From that one distinction, an entire ethic of freedom and dignity follows.",
          "The Epictetus AI guide in Virtue Guide speaks in that voice: direct, plain, and on fire with the conviction that you are freer than you think, if you would only stop carrying what is not yours to carry.",
        ],
      },
      sections: [
        {
          heading: "The one distinction that changes everything",
          body:
            "Epictetus opens the Enchiridion with what may be the most useful sentence in philosophy: \"Some things are in our control and others are not.\"\n\nUnderstand that one line, really understand it, and most of what you suffer becomes optional. The Epictetus AI guide is built around training that distinction until it becomes reflex.",
          italicQuote: "It is not what happens to you, but how you react to it that matters.",
          attrib: "Epictetus",
        },
        {
          heading: "Mentally stronger, not mentally numb",
          body:
            "Stoicism is sometimes confused with stoicism, the lower-case kind that means \"don't feel anything.\" That is not what Epictetus teaches. He teaches that you may feel everything, and still not be ruled by it. That distinction is the difference between repression and real strength.\n\nThe Epictetus AI guide will not pity you. It will help you see what is actually yours to act on, and let go of the rest. That is how people become mentally stronger.",
        },
        {
          heading: "Discipline as the road to freedom",
          body:
            "Epictetus came from slavery. He wrote about freedom as someone who knew exactly what it cost. His freedom was internal: the freedom of someone who had given up wishing the world were different and started using the one he had.\n\nIf your mind is a noisy place, Epictetus is the guide to start with. The work is plain, hard, and worth it.",
        },
      ],
    },
    nl: {
      eyebrow: "Epictetus AI",
      title: "Epictetus AI: De Dichotomie van Controle, Dagelijks Toegepast | Virtue Guide",
      description: "Epictetus AI in Virtue Guide. Voer een gesprek met een gids gevormd door de Discoursen en het Enchiridion: direct, helder, gedisciplineerde Stoïcijnse raad over wat wel en niet aan u is.",
      h1: { lead: "Epictetus,", accent: "en de vrijheid van loslaten wat niet van u is." },
      lede: "Epictetus had niets, en had op de een of andere manier alles. De Epictetus AI-gids in Virtue Guide brengt die stem in uw dag: het heldere onderricht van een vrijgemaakte slaaf over wat aan u is, wat niet, en hoe vrij te leven in beide gevallen.",
      sage: {
        name: "Epictetus",
        role: "De Vrijgemaakte Slaaf",
        dates: "Stoïcijns Filosoof · ca. 50–135 n.Chr.",
        image: IMG_EPICTETUS,
        paragraphs: [
          "Epictetus werd geboren als slaaf, in zijn jeugd verlamd, op volwassen leeftijd vrijgelaten, en werd één van de meest onverzettelijke leermeesters van het Stoïcisme die de wereld ooit kende. Zelf schreef hij niets; zijn Discoursen en het Enchiridion werden opgetekend door zijn leerling Arrianus.",
          "Zijn onderwerp is de dichotomie van controle: sommige dingen zijn aan u, andere niet. Uit die ene onderscheiding volgt een volledige ethiek van vrijheid en waardigheid.",
          "De Epictetus AI-gids in Virtue Guide spreekt in die stem: direct, helder, en gloeiend van de overtuiging dat u vrijer bent dan u denkt, als u maar zou stoppen met dragen wat niet van u is om te dragen.",
        ],
      },
      sections: [
        {
          heading: "De ene onderscheiding die alles verandert",
          body:
            "Epictetus opent het Enchiridion met wat misschien wel de nuttigste zin in de filosofie is: \"Sommige dingen zijn in onze macht, andere niet.\"\n\nBegrijp die ene regel, werkelijk, en het meeste van wat u lijdt wordt optioneel. De Epictetus AI-gids is gebouwd rond het trainen van die onderscheiding totdat het een reflex wordt.",
          italicQuote: "Het is niet wat u overkomt, maar hoe u erop reageert dat ertoe doet.",
          attrib: "Epictetus",
        },
        {
          heading: "Mentaal sterker, niet mentaal verdoofd",
          body:
            "Stoïcisme wordt soms verward met stoïcijns-zijn, het soort met een kleine letter dat \"niets voelen\" betekent. Dat is niet wat Epictetus leert. Hij leert dat u alles kunt voelen en er toch niet door beheerst wordt. Die onderscheiding is het verschil tussen onderdrukking en werkelijke kracht.\n\nDe Epictetus AI-gids zal u niet bemedelijden. Hij helpt u zien wat werkelijk aan u is om naar te handelen, en de rest los te laten. Zo worden mensen mentaal sterker.",
        },
        {
          heading: "Discipline als weg naar vrijheid",
          body:
            "Epictetus kwam uit slavernij. Hij schreef over vrijheid als iemand die precies wist wat het kostte. Zijn vrijheid was innerlijk: de vrijheid van iemand die had opgegeven te wensen dat de wereld anders was, en de wereld die hij had begon te gebruiken.\n\nAls uw geest een lawaaierige plek is, is Epictetus de gids om mee te beginnen. Het werk is helder, hard, en de moeite waard.",
        },
      ],
    },
  },
};

export function getSubPage(slug, lang = "en") {
  const entry = SUB_PAGES[slug];
  if (!entry) return null;
  const content = entry[lang] || entry.en;
  return { ...content, personJsonLd: entry.personJsonLd || null };
}
